import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Mail, Phone, Download, Loader2, Trash2, CalendarClock, X, Lock } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { Confirm } from "./Confirm";
import { toLocalInput } from "./inquiryUi";
import { http, apiError, formatDateTime, telHref, DIVISIONS } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtSize } from "../../components/site/FileUploader";
import { PROPOSAL_TAXONOMY } from "../../components/site/AssetMultiSelect";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";

export const OPP_STATUS = {
  new: "New", under_review: "Under Review", assigned: "Assigned", contacted: "Contacted", follow_up: "Follow-Up Required",
  in_discussion: "In Discussion", proposal_requested: "Proposal Requested", negotiation: "Negotiation", accepted: "Accepted",
  declined: "Declined", completed: "Completed", closed: "Closed",
};
const CLOSED = ["accepted", "declined", "completed", "closed"];
const Pill = ({ children, cls = "border-white/20 text-slate-300" }) => <span className={`font-mono text-[0.62rem] uppercase px-2 py-1 border ${cls}`}>{children}</span>;
const statusCls = (s) => (s === "new" ? "border-[var(--gold)] text-gold" : CLOSED.includes(s) ? "border-slate-500/50 text-slate-400" : "border-sky-400/40 text-sky-300");
const overdue = (o) => o.follow_up_at && !CLOSED.includes(o.status) && new Date(o.follow_up_at) <= new Date();
const Field = ({ k, v }) => (v ? <div className="grid grid-cols-[130px_1fr] gap-3 py-2.5"><dt className="text-slate-400">{k}</dt><dd className="text-slate-100 whitespace-pre-line break-words">{v}</dd></div> : null);

const download = async (o, f) => {
  try {
    const { data } = await http.get(`/admin/opportunities/${o.id}/files/${f.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error("Could not download the file.");
  }
};

const Detail = ({ o, team, owner, onChange, onDeleted }) => {
  const [note, setNote] = useState("");
  const [fu, setFu] = useState(toLocalInput(o.follow_up_at));
  const [busy, setBusy] = useState("");
  const base = `/admin/opportunities/${o.id}`;
  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      if (msg) toast.success(msg);
      onChange(data);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-8" data-testid="opp-detail">
      <div>
        <p className="font-mono text-xs text-gold" data-testid="opp-detail-ref">{o.ref}</p>
        <h2 className="font-display text-3xl mt-1">{o.subject}</h2>
        <div className="flex flex-wrap gap-2 mt-3"><Pill cls={statusCls(o.status)}>{OPP_STATUS[o.status]}</Pill><Pill>{o.category}</Pill><Pill cls={o.assigned_name ? "border-white/20 text-slate-200" : "border-rose-400/50 text-rose-300"}>{o.assigned_name || "Unassigned"}</Pill>{overdue(o) && <Pill cls="border-rose-400/60 text-rose-300">Follow-up overdue</Pill>}</div>
        <p className="text-xs text-slate-400 mt-3">Received {formatDateTime(o.created_at)}</p>
      </div>
      <section className="space-y-2 text-sm">
        <p className="text-slate-100 font-medium">{o.name} · {o.company}</p>
        <a href={`mailto:${o.email}`} className="flex items-center gap-2 text-gold hover:underline break-all"><Mail className="w-4 h-4 shrink-0" />{o.email}</a>
        {o.phone && <a href={telHref(o.phone)} className="flex items-center gap-2 text-slate-200 hover:text-gold"><Phone className="w-4 h-4" />{o.phone}</a>}
        <dl className="divide-y divide-white/10 border-y hairline mt-3">
          <Field k="Submitted from" v={o.division ? `${o.division} page` : "Business Opportunities page"} /><Field k="Website" v={o.website_url} /><Field k="Address" v={o.address} /><Field k="Industry" v={o.industry} />
          <Field k="Description" v={o.description} /><Field k="Requested of us" v={o.requested_action} />
          <Field k="Value / budget" v={o.value} /><Field k="Timeline" v={o.timeline} /><Field k="Comments" v={o.comments} />
          <Field k="Routed to" v={o.route === "sales" ? "Sales" : "General (info)"} />
        </dl>
      </section>
      <section data-testid="opp-asset-types">
        <p className="eyebrow mb-3">Proposal categories</p>
        {(o.asset_types || []).length ? (
          <ul className="space-y-1.5 text-sm">{o.asset_types.map((p) => <li key={`${p.division}|${p.type}`} className="text-slate-200"><span className="text-slate-400">{p.division} — </span>{p.type}</li>)}</ul>
        ) : <p className="text-sm text-slate-400">None selected.</p>}
      </section>
      <section data-testid="opp-files">
        <p className="eyebrow mb-3">Attachments ({(o.files || []).length})</p>
        {(o.files || []).length ? (
          <ul className="space-y-2">{o.files.map((f) => (
            <li key={f.id}><button type="button" onClick={() => download(o, f)} className="w-full flex items-center gap-3 p-3 border hairline hover:border-[var(--gold)] text-left transition-colors" data-testid={`opp-file-${f.id}`}>
              <Download className="w-4 h-4 text-gold shrink-0" /><span className="flex-1 min-w-0 truncate text-sm">{f.name}</span><span className="text-xs text-slate-400">{fmtSize(f.size)}</span>
            </button></li>))}
          </ul>
        ) : <p className="text-sm text-slate-400">No documents attached.</p>}
        <p className="text-xs text-slate-400 mt-2">Files are downloaded, never opened in the browser. Scan them with your antivirus before opening.</p>
      </section>
      <section className="grid sm:grid-cols-2 gap-4">
        {owner && (
          <div><label className="label">Assigned to</label>
            <select className="field" value={o.assigned_to || ""} disabled={busy === "assign"} onChange={(e) => run("assign", () => http.post(`${base}/assign`, { user_id: e.target.value || null }), "Assignment updated")} data-testid="opp-assign-select">
              <option value="">Unassigned</option>{team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></div>
        )}
        <div><label className="label">Status</label>
          <select className="field" value={o.status} disabled={busy === "status"} onChange={(e) => run("status", () => http.patch(base, { status: e.target.value }), "Status updated")} data-testid="opp-status-select">
            {Object.entries(OPP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
      </section>
      <section>
        <label className="label">Follow-up date</label>
        <div className="flex flex-wrap gap-2">
          <input type="datetime-local" className="field !w-auto flex-1 min-w-[200px]" value={fu} onChange={(e) => setFu(e.target.value)} data-testid="opp-followup-input" />
          <button className="btn-gold !py-2.5 !px-4" disabled={!fu || busy === "fu"} onClick={() => run("fu", () => http.patch(base, { follow_up_at: new Date(fu).toISOString() }), "Follow-up scheduled")} data-testid="opp-followup-save"><CalendarClock className="w-4 h-4" /> Save</button>
          {o.follow_up_at && <button className="btn-ghost !py-2.5 !px-3" onClick={async () => { if (await run("fu", () => http.patch(base, { clear_follow_up: true }), "Follow-up cleared")) setFu(""); }} data-testid="opp-followup-clear"><X className="w-4 h-4" /></button>}
        </div>
        <p className="text-xs text-slate-400 mt-2">The assigned person gets an email reminder when it's due.</p>
      </section>
      <section data-testid="opp-notes">
        <p className="eyebrow mb-3 flex items-center gap-2"><Lock className="w-3 h-3" /> Internal notes (staff only)</p>
        <ul className="space-y-3 mb-4">{[...(o.notes_log || [])].reverse().map((n) => (
          <li key={n.id} className="border-l-2 border-white/15 pl-4 text-sm"><p className="text-slate-200 whitespace-pre-line">{n.text}</p><p className="text-xs text-slate-400 mt-1">{n.author} · {formatDateTime(n.at)}</p></li>))}
        </ul>
        <textarea rows={3} className="field" placeholder="Add an internal note. Never shared with the submitter." value={note} onChange={(e) => setNote(e.target.value)} data-testid="opp-note-input" />
        <button className="btn-ghost !py-2.5 !px-4 mt-2" disabled={!note.trim() || busy === "note"} onClick={async () => { if (await run("note", () => http.post(`${base}/notes`, { text: note }), "Note added")) setNote(""); }} data-testid="opp-note-add">{busy === "note" && <Loader2 className="w-4 h-4 animate-spin" />} Add note</button>
      </section>
      {owner && (
        <Confirm title="Delete this opportunity?" text="The submission, its notes and access to its documents will be removed." onConfirm={async () => { await http.delete(base); toast.success("Deleted"); onDeleted(); }}>
          <button className="text-xs text-rose-300 hover:underline flex items-center gap-1.5" data-testid="opp-delete"><Trash2 className="w-3 h-3" /> Delete submission</button>
        </Confirm>
      )}
    </div>
  );
};

export default function OpportunitiesAdmin() {
  const { user } = useAuth();
  const owner = user?.role === "owner";
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const query = Object.fromEntries(["status", "category", "division", "asset_type", "q"].filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
  const openId = params.get("id");
  const { data, isLoading } = useQuery({ queryKey: ["opportunities", query], queryFn: async () => (await http.get("/admin/opportunities", { params: query })).data });
  const { data: teamData } = useQuery({ queryKey: ["team"], queryFn: async () => (await http.get("/admin/inquiries/team")).data });
  const { data: detail } = useQuery({ queryKey: ["opportunity", openId], enabled: !!openId, retry: 0, queryFn: async () => (await http.get(`/admin/opportunities/${openId}`)).data });
  const items = Array.isArray(data?.items) ? data.items : [];
  const cats = Array.isArray(data?.categories) ? data.categories : [];
  const team = teamData?.items || [];
  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const refresh = (doc) => {
    if (doc?.id) qc.setQueryData(["opportunity", doc.id], doc);
    ["opportunities", "admin-stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  return (
    <div data-testid="admin-opportunities">
      <AdminTitle title="Business Opportunities" sub={owner ? "Partnership, contract and project submissions with their documents." : "Business opportunities assigned to you."} />
      <div className="flex flex-col lg:flex-row lg:flex-wrap gap-3 mb-6">
        <form className="relative flex-1 lg:min-w-[320px]" onSubmit={(e) => { e.preventDefault(); set({ q: q.trim() }); }}>
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="field !py-2.5 !pl-10" placeholder="Search name, company, subject or OPP ref… (Enter)" value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => set({ q: q.trim() })} data-testid="opp-search-input" />
        </form>
        <select className="field xl:!w-52 !py-2.5" value={params.get("division") || ""} onChange={(e) => set({ division: e.target.value })} data-testid="opp-filter-division">
          <option value="">All divisions</option>{DIVISIONS.map((d) => <option key={d.key} value={d.name}>{d.name}</option>)}
        </select>
        <select className="field xl:!w-60 !py-2.5" value={params.get("asset_type") || ""} onChange={(e) => set({ asset_type: e.target.value })} data-testid="opp-filter-asset-type">
          <option value="">All asset types</option>
          {Object.entries(PROPOSAL_TAXONOMY).map(([dv, types]) => <optgroup key={dv} label={dv}>{types.map((t) => <option key={t} value={t}>{t}</option>)}</optgroup>)}
        </select>
        <select className="field xl:!w-64 !py-2.5" value={params.get("category") || ""} onChange={(e) => set({ category: e.target.value })} data-testid="opp-filter-category">
          <option value="">All categories</option>{cats.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select className="field xl:!w-52 !py-2.5" value={params.get("status") || ""} onChange={(e) => set({ status: e.target.value })} data-testid="opp-filter-status">
          <option value="">All statuses</option><option value="open">All open</option>{Object.entries(OPP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {isLoading ? <p className="text-slate-400">Loading…</p> : !items.length ? (
        <p className="border border-dashed border-white/20 p-12 text-center text-slate-300" data-testid="opp-empty">{owner ? "No business opportunities yet." : "No opportunities are assigned to you right now."}</p>
      ) : (
        <div className="border hairline divide-y divide-white/10" data-testid="opp-list">
          {items.map((o) => (
            <button key={o.id} onClick={() => set({ id: o.id })} className={`w-full text-left p-4 md:p-5 bg-[var(--ink-2)] hover:bg-[var(--ink-3)] transition-colors ${!o.assigned_to ? "border-l-2 border-l-rose-400" : ""}`} data-testid={`opp-row-${o.id}`}>
              <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-gold">{o.ref}</span><Pill cls={statusCls(o.status)}>{OPP_STATUS[o.status]}</Pill><Pill>{o.category}</Pill>{(o.divisions?.length ? o.divisions : o.division ? [o.division] : []).map((dv) => <Pill key={dv} cls="border-[rgba(212,175,55,0.4)] text-[#e9cf7a]">{dv.replace(" Division", "")}</Pill>)}<Pill cls={o.assigned_name ? "border-white/20 text-slate-200" : "border-rose-400/50 text-rose-300"}>{o.assigned_name || "Unassigned"}</Pill>{overdue(o) && <Pill cls="border-rose-400/60 text-rose-300">Overdue</Pill>}{(o.files || []).length > 0 && <Pill>{o.files.length} file{o.files.length > 1 ? "s" : ""}</Pill>}</div>
              <p className="mt-2 text-slate-100 font-medium truncate">{o.subject}</p>
              {(o.asset_types || []).length > 0 && <p className="text-xs text-slate-300 truncate mt-0.5">{o.asset_types.map((p) => p.type).join(" · ")}</p>}
              <p className="text-sm text-slate-300 truncate">{o.name} · {o.company} · {o.email}</p>
              <p className="text-xs text-slate-400 mt-1">Received {formatDateTime(o.created_at)}</p>
            </button>
          ))}
        </div>
      )}
      <Sheet open={!!openId} onOpenChange={(v) => !v && set({ id: "" })}>
        <SheetContent side="right" className="w-full sm:max-w-xl bg-[var(--ink)] border-white/10 overflow-y-auto" data-testid="opp-sheet">
          <SheetHeader className="sr-only"><SheetTitle>Opportunity details</SheetTitle><SheetDescription>Documents, assignment, status, follow-up and notes</SheetDescription></SheetHeader>
          {detail ? <Detail key={`${detail.id}-${detail.follow_up_at}`} o={detail} team={team} owner={owner} onChange={refresh} onDeleted={() => { set({ id: "" }); refresh(); }} /> : <p className="text-slate-400 pt-10">Loading… (or this opportunity is not assigned to you)</p>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
